// LongTermIssuesCard v0.2.2 — collapsible and persisted per-visitor.
// Keeps the long-duration surface separate from active issues, while matching
// the active row treatment for readability and accessibility.
import { useId } from 'react';
import type { ProviderItem, StateResponse } from '../lib/api.js';
import { isLongTerm, timeAgo } from '../lib/formatTime.js';
import { providerDisplayName } from '../lib/providerDisplayName.js';
import { getProviderSeverityTone, resolveProviderState } from '../lib/providerState.js';
import { statusPageUrl } from '../lib/statusPageUrl.js';
import ExternalIndicator from './ExternalIndicator.js';
import ExternalStatusIcon from './ExternalStatusIcon.js';
import ProviderInfoTooltip from './ProviderInfoTooltip.js';
import CommandPanel from './ui/CommandPanel.js';
import { usePersistedPanelState } from '../hooks/usePersistedPanelState.js';
import SectionHeader from './ui/SectionHeader.js';
import StatusGlow from './ui/StatusGlow.js';

interface Props {
  all: ProviderItem[];
  state: StateResponse['providers'];
}

const SEV_LABEL: Record<number, string> = { 1: 'DEG', 2: 'MAJ', 3: 'DOWN' };

export default function LongTermIssuesCard({ all, state }: Props) {
  const [expanded, setExpanded] = usePersistedPanelState('panel:long-term-issues', true);
  const contentId = useId();
  const getState = (p: ProviderItem) => resolveProviderState(state, p);
  const issues = all
    .map((p) => {
      const providerState = getState(p);
      return {
        p,
        sev: providerState?.severity ?? 0,
        msg: providerState?.message ?? '',
        since: providerState?.since,
        updatedAt: providerState?.updated_at,
        tone: getProviderSeverityTone(state, p),
      };
    })
    .filter((row) => row.sev > 0 && isLongTerm(row.updatedAt ?? row.since))
    .sort(
      (a, b) =>
        a.p.tier - b.p.tier ||
        b.sev - a.sev ||
        (a.since ?? '').localeCompare(b.since ?? '') ||
        a.p.name.localeCompare(b.p.name),
    );

  if (issues.length === 0) return null;

  return (
    <CommandPanel className="border border-border">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <span aria-hidden="true" className="text-white">
              ◌
            </span>
            <span>LONG-TERM ISSUES</span>
          </span>
        }
        right={<span className="text-white font-normal">{issues.length} · 24h+</span>}
        expanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        contentId={contentId}
      />
      {expanded && (
        <div className="divide-y divide-border/60 text-xs font-mono">
          {issues.map(({ p, sev, msg, since, tone }) => {
            const sevLabel = SEV_LABEL[sev] ?? '?';
            const isMajor = tone === 2;
            const displayName = providerDisplayName(p.slug, p.name);
            return (
              <div
                key={p.slug}
                title={msg ? `${p.name} — ${msg}` : p.name}
                className={`flex items-center gap-3 px-3 py-2 min-w-0 hover:bg-surface2 ${
                  isMajor ? 'bg-bad/20' : ''
                }`}
              >
                <span className="text-white shrink-0 w-8 font-bold">T{p.tier}</span>
                <span className="flex items-center gap-1 min-w-0 shrink-0 max-w-[18ch]">
                  <span className="text-white font-semibold truncate">{displayName}</span>
                  {p.description && <ProviderInfoTooltip description={p.description} />}
                </span>
                <StatusGlow severity={tone}>{sevLabel}</StatusGlow>
                <span className="text-white shrink-0 whitespace-nowrap">
                  {since ? timeAgo(since) : '—'}
                </span>
                {msg && <span className="text-white truncate flex-1 min-w-0 text-right">{msg}</span>}
                <span className="shrink-0 flex items-center gap-2">
                  {p.source_type === 'external' ? (
                    <ExternalIndicator href={statusPageUrl(p)} label={p.name} />
                  ) : null}
                  <ExternalStatusIcon
                    href={statusPageUrl(p)}
                    label={`Open ${p.name} status page`}
                  />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </CommandPanel>
  );
}
